import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Upload,
    X,
    Zap,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, errorMessage, publicAssetUrl, uploadFile } from "@/lib/api";

const MAX_FILES = 30;
const MAX_MB = 5;
const ALLOWED_MIMES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);

/** Derive a human-friendly title from a filename ("nesto-diwali-25off.jpg" → "Nesto Diwali 25 Off"). */
const titleFromFilename = (name) =>
    String(name || "")
        .replace(/\.[^.]+$/, "")
        .replace(/[-_]+/g, " ")
        .replace(/([a-z])(\d)/gi, "$1 $2")
        .replace(/(\d)([a-z])/gi, "$1 $2")
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

/** datetime-local requires YYYY-MM-DDTHH:mm (local time, no tz suffix). */
const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultValidTo = () => toLocalInput(new Date(Date.now() + 7 * 24 * 3600 * 1000));

/**
 * Bulk create OFFER posts.
 *
 * Flow:
 *   1) Admin drops N image files → each uploads to POST /api/uploads?purpose=poster
 *      immediately, storing the returned URL on the row.
 *   2) Admin fills Title/Brand/District/Discount/ValidUntil per row (or via
 *      the bulk-apply toolbar).
 *   3) "Create N offers" → sequentially POSTs /api/posts with type=OFFER,
 *      status=PUBLISHED, poster_image_url=<uploaded>, category_slug inherited
 *      from the picked brand, business_name=brand_name.
 *
 * No new backend endpoint; reuses existing /api/uploads and /api/posts.
 */
export default function BulkOffersDialog({ open, onOpenChange, onDone }) {
    // Rows: { id, file, filename, previewUrl, uploadUrl, uploadError, uploading,
    //         title, shopId, districtSlug, discount, validTo,
    //         status: 'idle'|'creating'|'created'|'failed', message, createdId }
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [summary, setSummary] = useState(null);
    const [brands, setBrands] = useState([]);
    const [districts, setDistricts] = useState([]);
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Toolbar
    const [bulkDistrict, setBulkDistrict] = useState("");
    const [bulkValidTo, setBulkValidTo] = useState("");
    const [bulkDiscount, setBulkDiscount] = useState("");

    // Fetch reference data once when dialog opens
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [bs, ds] = await Promise.all([
                    api.get("/partner-shops?limit=200"),
                    api.get("/districts"),
                ]);
                const items = bs.data?.items || bs.data || [];
                setBrands(items);
                setDistricts(ds.data || []);
            } catch (e) {
                toast.error("Failed to load brands / districts");
            }
        })();
    }, [open]);

    const brandsById = useMemo(() => {
        const m = new Map();
        brands.forEach((b) => m.set(b.id, b));
        return m;
    }, [brands]);

    const nowIsoRef = useRef(new Date().toISOString());

    const validateFile = (file) => {
        if (!ALLOWED_MIMES.has(file.type)) return `Unsupported "${file.type || "unknown"}". PNG/JPG/WEBP only.`;
        if (file.size > MAX_MB * 1024 * 1024) return `Too large (${(file.size / 1048576).toFixed(1)} MB). Max ${MAX_MB} MB.`;
        return null;
    };

    const uploadOne = async (rowId, file) => {
        try {
            const res = await uploadFile(file, "poster");
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, uploadUrl: res.url, uploading: false } : r)));
        } catch (e) {
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, uploadError: errorMessage(e), uploading: false } : r)));
        }
    };

    const addFiles = (fileList) => {
        setSummary(null);
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        const slots = MAX_FILES - rows.length;
        if (slots <= 0) {
            toast.error(`Max ${MAX_FILES} rows per batch — remove some first.`);
            return;
        }
        const accepted = incoming.slice(0, slots);
        if (incoming.length > slots) toast.error(`Only ${slots} slot(s) left — added first ${slots}.`);

        const built = accepted.map((f) => {
            const err = validateFile(f);
            return {
                id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
                file: f,
                filename: f.name,
                previewUrl: URL.createObjectURL(f),
                uploadUrl: null,
                uploadError: err,
                uploading: !err,
                title: titleFromFilename(f.name),
                shopId: "",
                districtSlug: "",
                discount: "",
                validTo: defaultValidTo(),
                status: "idle",
                message: null,
                createdId: null,
            };
        });
        setRows((prev) => [...prev, ...built]);
        if (inputRef.current) inputRef.current.value = "";
        // kick off async uploads
        built.filter((r) => !r.uploadError).forEach((r) => uploadOne(r.id, r.file));
    };

    const updateRow = (id, patch) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const removeRow = (id) =>
        setRows((prev) => {
            const gone = prev.find((r) => r.id === id);
            if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
            return prev.filter((r) => r.id !== id);
        });

    const applyBulkDistrict = () => {
        if (!bulkDistrict) return;
        setRows((prev) => prev.map((r) => ({ ...r, districtSlug: bulkDistrict })));
    };
    const applyBulkValidTo = () => {
        if (!bulkValidTo) return;
        setRows((prev) => prev.map((r) => ({ ...r, validTo: bulkValidTo })));
    };
    const applyBulkDiscount = () => {
        if (bulkDiscount === "") return;
        setRows((prev) => prev.map((r) => ({ ...r, discount: bulkDiscount })));
    };

    const rowValidation = (r) => {
        if (r.uploadError) return "Upload failed";
        if (r.uploading || !r.uploadUrl) return "Poster still uploading";
        if (!r.title.trim()) return "Title required";
        if (!r.shopId) return "Brand required";
        if (!r.districtSlug) return "District required";
        return null;
    };
    const anyRowsValid = rows.length > 0 && rows.every((r) => !rowValidation(r));

    const submit = async () => {
        if (running) return;
        if (!anyRowsValid) {
            toast.error("Fill in every required field before creating");
            return;
        }
        setRunning(true);
        setSummary(null);
        const nowIso = new Date().toISOString();
        let ok = 0;
        let failed = 0;
        for (const r of rows) {
            updateRow(r.id, { status: "creating", message: null });
            const brand = brandsById.get(r.shopId);
            const discountNum = r.discount === "" ? null : Number(r.discount);
            const body = {
                type: "OFFER",
                title: r.title.trim(),
                poster_image_url: r.uploadUrl,
                category_slug: brand?.category_slug || null,
                district_slug: r.districtSlug,
                business_name: brand?.brand_name || null,
                status: "PUBLISHED",
                valid_from: nowIso,
                valid_to: new Date(r.validTo).toISOString(),
                details: {
                    ...(Number.isFinite(discountNum) && discountNum !== null ? { discount_percent: discountNum } : {}),
                    gallery: [],
                },
            };
            try {
                const res = await api.post("/posts", body);
                ok += 1;
                updateRow(r.id, { status: "created", createdId: res.data?.id });
            } catch (e) {
                failed += 1;
                updateRow(r.id, { status: "failed", message: errorMessage(e) });
            }
        }
        setSummary({ ok, failed, skipped: 0 });
        setRunning(false);
        if (ok > 0) toast.success(`${ok} offer${ok === 1 ? "" : "s"} created`);
        onDone?.();
    };

    const handleClose = (nextOpen) => {
        if (!nextOpen && !running) {
            rows.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
            setRows([]);
            setSummary(null);
            setBulkDistrict("");
            setBulkValidTo("");
            setBulkDiscount("");
        }
        onOpenChange(nextOpen);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (running) return;
        addFiles(e.dataTransfer?.files);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                className="max-w-5xl"
                data-testid="bulk-offers-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Zap size={16} className="text-orange-600" /> Bulk create offers
                    </DialogTitle>
                    <DialogDescription>
                        Drop posters, pick brand + district per row, then publish them all
                        in one shot. Max {MAX_FILES} files · {MAX_MB} MB each · PNG/JPG/WEBP.
                    </DialogDescription>
                </DialogHeader>

                {/* Dropzone */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => !running && inputRef.current?.click()}
                    className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center transition ${isDragOver ? "border-orange-500 bg-orange-50" : "border-neutral-300 bg-neutral-50 hover:border-orange-400 hover:bg-orange-50/50"}`}
                    data-testid="bulk-offers-dropzone"
                >
                    <Upload size={22} className="text-orange-600" />
                    <div className="text-sm font-semibold text-neutral-800">
                        Drop poster images — one row per file
                    </div>
                    <div className="text-xs text-neutral-500">or click to select</div>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => addFiles(e.target.files)}
                    />
                </div>

                {/* Bulk-apply toolbar */}
                {rows.length > 1 && (
                    <div className="grid gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3 sm:grid-cols-3">
                        <div className="flex items-center gap-1">
                            <Select value={bulkDistrict} onValueChange={setBulkDistrict}>
                                <SelectTrigger className="h-8 text-xs" data-testid="bulk-offers-apply-district-select">
                                    <SelectValue placeholder="District…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {districts.map((d) => (
                                        <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={applyBulkDistrict}
                                disabled={!bulkDistrict}
                                data-testid="bulk-offers-apply-district"
                                className="h-8"
                            >
                                Apply
                            </Button>
                        </div>
                        <div className="flex items-center gap-1">
                            <Input
                                type="datetime-local"
                                value={bulkValidTo}
                                onChange={(e) => setBulkValidTo(e.target.value)}
                                className="h-8 text-xs"
                                data-testid="bulk-offers-apply-validto-input"
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={applyBulkValidTo}
                                disabled={!bulkValidTo}
                                data-testid="bulk-offers-apply-validto"
                                className="h-8"
                            >
                                Apply
                            </Button>
                        </div>
                        <div className="flex items-center gap-1">
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="Discount %…"
                                value={bulkDiscount}
                                onChange={(e) => setBulkDiscount(e.target.value)}
                                className="h-8 text-xs"
                                data-testid="bulk-offers-apply-discount-input"
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={applyBulkDiscount}
                                disabled={bulkDiscount === ""}
                                data-testid="bulk-offers-apply-discount"
                                className="h-8"
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                )}

                {/* Row table */}
                {rows.length > 0 && (
                    <div className="max-h-[420px] overflow-y-auto rounded-xl border border-neutral-200">
                        <table className="w-full text-left text-xs">
                            <thead className="sticky top-0 bg-neutral-50">
                                <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-wide text-neutral-500">
                                    <th className="w-16 px-2 py-2">Poster</th>
                                    <th className="px-2 py-2">Title *</th>
                                    <th className="px-2 py-2">Brand *</th>
                                    <th className="px-2 py-2">District *</th>
                                    <th className="w-20 px-2 py-2">% off</th>
                                    <th className="w-40 px-2 py-2">Valid until</th>
                                    <th className="w-24 px-2 py-2">Status</th>
                                    <th className="w-10 px-2 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const err = rowValidation(r);
                                    return (
                                        <tr
                                            key={r.id}
                                            className="border-b border-neutral-100 align-top last:border-none"
                                            data-testid={`bulk-offers-row-${r.filename}`}
                                        >
                                            <td className="px-2 py-2">
                                                <div className="relative h-16 w-12 overflow-hidden rounded-md bg-neutral-100 ring-1 ring-neutral-200">
                                                    <img src={r.previewUrl} alt="" className="h-full w-full object-cover" />
                                                    {r.uploading && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                                                            <Loader2 size={14} className="animate-spin" />
                                                        </div>
                                                    )}
                                                </div>
                                                {r.uploadError && <div className="mt-1 text-[9px] font-semibold text-rose-600">{r.uploadError}</div>}
                                            </td>
                                            <td className="px-2 py-2">
                                                <Input
                                                    value={r.title}
                                                    onChange={(e) => updateRow(r.id, { title: e.target.value })}
                                                    className="h-8 text-xs"
                                                    placeholder="Offer title"
                                                    data-testid={`bulk-offers-row-${r.filename}-title`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <Select
                                                    value={r.shopId}
                                                    onValueChange={(v) => updateRow(r.id, { shopId: v })}
                                                >
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`bulk-offers-row-${r.filename}-brand`}>
                                                        <SelectValue placeholder="Pick brand…" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-72">
                                                        {brands.map((b) => (
                                                            <SelectItem key={b.id} value={b.id}>
                                                                {b.brand_name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <Select
                                                    value={r.districtSlug}
                                                    onValueChange={(v) => updateRow(r.id, { districtSlug: v })}
                                                >
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`bulk-offers-row-${r.filename}-district`}>
                                                        <SelectValue placeholder="District…" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-72">
                                                        {districts.map((d) => (
                                                            <SelectItem key={d.slug} value={d.slug}>
                                                                {d.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={r.discount}
                                                    onChange={(e) => updateRow(r.id, { discount: e.target.value })}
                                                    className="h-8 text-xs"
                                                    placeholder="—"
                                                    data-testid={`bulk-offers-row-${r.filename}-discount`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <Input
                                                    type="datetime-local"
                                                    value={r.validTo}
                                                    onChange={(e) => updateRow(r.id, { validTo: e.target.value })}
                                                    className="h-8 text-xs"
                                                    data-testid={`bulk-offers-row-${r.filename}-validto`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                {r.status === "idle" && (
                                                    err ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600" title={err}>
                                                            <AlertTriangle size={11} /> {err}
                                                        </span>
                                                    ) : (
                                                        <span className="text-[11px] text-neutral-400">Ready</span>
                                                    )
                                                )}
                                                {r.status === "creating" && (
                                                    <span className="inline-flex items-center gap-1 text-orange-600">
                                                        <Loader2 size={11} className="animate-spin" /> Creating
                                                    </span>
                                                )}
                                                {r.status === "created" && (
                                                    <span className="inline-flex items-center gap-1 font-semibold text-emerald-600" title={r.createdId}>
                                                        <CheckCircle2 size={12} /> Created
                                                    </span>
                                                )}
                                                {r.status === "failed" && (
                                                    <span className="inline-flex items-center gap-1 text-rose-600" title={r.message || "Failed"}>
                                                        <AlertTriangle size={11} /> Failed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                {!running && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRow(r.id)}
                                                        aria-label={`Remove ${r.filename}`}
                                                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-rose-600"
                                                        data-testid={`bulk-offers-row-${r.filename}-remove`}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {summary && (
                    <div
                        className="rounded-xl bg-neutral-50 px-3 py-2 text-xs text-neutral-700"
                        data-testid="bulk-offers-summary"
                    >
                        <span className="font-semibold text-emerald-700">Created {summary.ok}</span>
                        {" · "}
                        <span className={summary.failed ? "font-semibold text-rose-700" : ""}>
                            Failed {summary.failed}
                        </span>
                        {" · "}
                        <span className="text-neutral-500">Skipped {summary.skipped}</span>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={running}
                    >
                        {summary ? "Close" : "Cancel"}
                    </Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={running || !anyRowsValid}
                        className="nest-gradient text-white"
                        data-testid="bulk-offers-confirm-btn"
                    >
                        {running ? (
                            <><Loader2 size={14} className="mr-1 animate-spin" /> Creating…</>
                        ) : (
                            <><Zap size={14} className="mr-1" /> Create {rows.length} offer{rows.length === 1 ? "" : "s"}</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
